/**
 * Talking to the account API.
 *
 * Every call sends the session cookie, which is HttpOnly — this file cannot
 * read it, and neither can anything else running on the page. That is the
 * point: there is no token in JavaScript to steal.
 */

const JSON_HEADERS = { 'Content-Type': 'application/json' };

class AccountError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function call(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(path, {
      method,
      headers: JSON_HEADERS,
      // Without this the browser omits the session cookie and every request
      // looks anonymous.
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new AccountError('Could not reach the server. Check your connection.', {
      code: 'offline',
    });
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    /* some responses have no body */
  }

  if (!response.ok) {
    throw new AccountError(payload?.message ?? 'Something went wrong.', {
      code: payload?.error ?? payload?.code ?? 'error',
      status: response.status,
    });
  }

  // A 200 that isn't JSON is not this API answering. A static host serving the
  // SPA's index.html for every path, a captive portal, a proxy error page —
  // they all look like success and would otherwise be read as "no account".
  if (payload === null) {
    throw new AccountError('Could not reach the server.', { code: 'offline' });
  }
  return payload;
}

export const register = (email, password) =>
  call('/api/auth/register', { method: 'POST', body: { email, password } });

export const login = (email, password) =>
  call('/api/auth/login', { method: 'POST', body: { email, password } });

export const logout = () => call('/api/auth/logout', { method: 'POST' });

export const me = () => call('/api/auth/me');

/**
 * Ask for a reset link. Always succeeds, whether or not the address has an
 * account — the server refuses to say which, and so does this.
 */
export const requestPasswordReset = (email) =>
  call('/api/auth/forgot', { method: 'POST', body: { email } });

export const checkResetToken = (token) =>
  call(`/api/auth/reset?token=${encodeURIComponent(token)}`);

export const resetPassword = (token, password) =>
  call('/api/auth/reset', { method: 'POST', body: { token, password } });

export const fetchProgress = () => call('/api/auth/progress');

export const pushProgress = (progress) =>
  call('/api/auth/progress', { method: 'PUT', body: { progress } });

export const chatStatus = () => call('/api/chat/status');

// ── Billing ─────────────────────────────────────────────────────────────────

/** Prices and whether checkout is wired up. Safe to call signed out. */
export const billingConfig = () => call('/api/billing/config');

/**
 * Both of these return a Stripe-hosted URL to send the browser to. Neither
 * grants anything — coming back from Stripe only prompts the app to re-ask the
 * server what this account is entitled to.
 */
export const startCheckout = (plan) =>
  call('/api/billing/checkout', { method: 'POST', body: { plan } });

export const openBillingPortal = () => call('/api/billing/portal', { method: 'POST' });

export { AccountError };

/**
 * Ask for another confirmation email.
 *
 * Always resolves. The server answers the same way whether it sent anything or
 * not — it will not say whether an address is already confirmed or is being
 * throttled — so there is nothing here worth turning into an error state.
 */
export async function resendVerification() {
  try {
    const response = await fetch('/api/auth/verify/resend', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Erase the account. `confirmation` is the password, or — for accounts made
 * through Google, which have none — the email address typed out.
 */
export const deleteAccount = (confirmation) =>
  call('/api/auth/account', { method: 'DELETE', body: { confirmation } });
